import { cloneDeep } from 'lodash';
import { BaseVector, Vector } from '../../../../../elements/base/vector/vector';
import {
  Coords,
  DragConstraintInstance,
} from '../../../../../elements/resource/types/shape.type';
import { PointController } from '../../../../../elements/util/point-controller/point-controller';
import {
  Easing,
  IncrementController,
} from '../../../../animation/frame/increment/controller/increment.controller';
import { Circle } from '../../primitive/circle-element';
import { PathElement } from '../../primitive/path-element';
import { ArcItem, CurveItem, PathItem } from '../path-shape.types';
import {
  HandShapeNext,
  HandShapeSectionDescriptorNew,
} from './hand-shape-next';

const { PI, sqrt, pow, asin } = Math;

export class HandSectionNext {
  xIncrement: IncrementController;
  yIncrement: IncrementController;
  pc: PointController;
  nextAngle: number;
  prevAngle: number;
  topStroke: PathElement;
  sideStroke: PathElement;
  bottomStroke: PathElement;
  startCircle: Circle;
  endCircle: Circle;
  cornerCircle: Circle;

  get amIFirst() {
    return this.index == 0;
  }

  get amILast() {
    return this.index == this.handShape.lastSectionIndex;
  }
  get x() {
    return this.config.x || 0;
  }
  get y() {
    return this.config.y || 0;
  }

  set x(val: number) {
    this.config.x = val;
  }

  set y(val: number) {
    this.config.y = val;
  }

  get pcs() {
    return [this.pc, this.rPc, this.d1PC, this.pc50].filter(pc => !!pc);
  }

  get r() {
    return this.config.r || 0;
  }
  set r(val: number) {
    this.config.r = val;
  }

  get flat() {
    return !this.amIFirst && !this.handShape.isClosed;
  }

  get d1() {
    // return ((this.config.d1 || 0) * PI) / 2;
    return this.config.d1 || 0;
  }

  set d1(val: number) {
    this.config.d1 = val;
  }

  get d2() {
    return this.config.d2 || 0;
  }

  get corner() {
    return !!this.config.corner;
  }

  get sections() {
    return this.handShape.handSections;
  }

  get prev(): HandSectionNext {
    if (0 < this.index) {
      return this.sections[this.index - 1];
    }
    return this.handShape.isClosed
      ? this.sections[this.sections.length - 1]
      : this.next;
  }

  get relX() {
    return this.x - this.prev.x;
  }

  get relY() {
    return this.y - this.prev.y;
  }

  get prevStrict(): HandSectionNext {
    if (0 < this.index) {
      return this.sections[this.index - 1];
    }
    return this.handShape.isClosed
      ? this.sections[this.sections.length - 1]
      : undefined;
  }

  get next(): HandSectionNext {
    if (this.index < this.sections.length - 1) {
      return this.sections[this.index + 1];
    }
    return this.handShape.isClosed ? this.sections[0] : this.prev;
  }

  get _next(): HandSectionNext {
    if (this.index < this.sections.length - 1) {
      return this.sections[this.index + 1];
    }
  }

  get nextStrict(): HandSectionNext {
    if (this.index < this.sections.length - 1) {
      return this.sections[this.index + 1];
    }
    return this.handShape.isClosed ? this.sections[0] : undefined;
  }

  get id() {
    return `section.${this.index}`;
  }

  get coords(): Coords {
    return [this.x, this.y];
  }

  get isCW() {
    if (this.handShape.handSections.length == 2) {
      return false;
    }

    if (!this.prevStrict) {
      return this.next.isCW;
    }

    if (!this.nextStrict) {
      return this.prev.isCW;
    }

    const [px, py] = this.prev.coords;
    const [nx, ny] = this.next.coords;

    const a1 = this.handShape.getAngle(px - this.x, py - this.y);
    const a2 = this.handShape.getAngle(nx - this.x, ny - this.y);

    if (a1 < PI) {
      return a1 < a2 ? a2 < a1 + PI : false;
    } else {
      return a1 < a2 || a2 < a1 - PI;
    }
  }

  get curve() {
    return isNaN(this.config.curve) ? 0 : (this.config.curve * PI) / 4;
  }

  get lastAbsCoords() {
    return this.handShape.handSections.reduce(
      (sum, hs) => {
        sum[0] += hs.x;
        sum[1] += hs.y;
        return sum;
      },
      [0, 0],
    );
  }

  get prevVectorCoords(): Coords {
    if (this.amIFirst) {
      if (this.handShape.isClosed) {
        const [x, y] = this.lastAbsCoords;
        return [x, y];
      }
      return [this.next.x, this.next.y];
    }
    return [-this.x, -this.y];
  }
  get prevVectorLength() {
    const [x, y] = this.prevVectorCoords;
    return sqrt(pow(x, 2) + pow(y, 2));
  }
  get nextVectorCoords(): Coords {
    if (this.amILast) {
      if (this.handShape.isClosed) {
        const [x, y] = this.lastAbsCoords;
        return [-x, -y];
      }

      return [-this.x, -this.y];
    }
    return [this.next.x, this.next.y];
  }

  get nextVectorLength() {
    const [x, y] = this.nextVectorCoords;
    return sqrt(pow(x, 2) + pow(y, 2));
  }

  rPc: PointController;
  d1PC: PointController;

  pc50: PointController;

  baseDragMode = false;

  get cs() {
    return this.handShape.cs;
  }

  constructor(
    public readonly handShape: HandShapeNext,
    public config: HandShapeSectionDescriptorNew,
    public index: number,
  ) {
    this.baseDragMode = !this.handShape.isClosed && this.index == 0;
  }

  unMovedR: number;
  showRC() {
    this.pcs.map(pc => pc.show());
  }

  hideRC() {
    this.pcs.map(pc => pc.hide());
  }

  get absoluteX() {
    return this.handShape.x + this.offsetX + this.x;
  }

  get absoluteY() {
    return this.handShape.y + this.offsetY + this.y;
  }

  get absX() {
    return this.offsetX + this.x;
  }
  get absY() {
    return this.offsetY + this.y;
  }

  get offsetX() {
    if (this.amIFirst) {
      return 0;
    }
    return this.prev.absX;
  }

  get offsetY() {
    if (this.amIFirst) {
      return 0;
    }
    return this.prev.absY;
  }

  get rpcAngle() {
    let vector: Vector;

    if (this.handShape.isClosed) {
      // -- // -- //
      if (this.amIFirst) {
        const [x, y] = this.lastAbsCoords;
        vector = new BaseVector([x, y]).add(
          new BaseVector([this.next.x, this.next.y]),
        );
      } else if (this.amILast) {
        const [x, y] = this.lastAbsCoords;
        vector = new BaseVector([-x, -y]).add(
          new BaseVector([-this.x, -this.y]),
        );
      } else {
        vector = new BaseVector([-this.x, -this.y]).add(
          new BaseVector([this.next.x, this.next.y]),
        );
      }
    } else {
      if (this.amIFirst) {
        vector = new BaseVector([this.next.x, this.next.y]);
      } else if (this.amILast) {
        vector = new BaseVector([this.x, this.y]);
      } else {
        vector = new BaseVector([-this.x, -this.y]).add(
          new BaseVector([this.next.x, this.next.y]),
        );
      }
    }

    vector.rotate(Math.PI);
    vector.reScale(this.r);

    return vector.getAngle();
  }

  afterInit() {
    this.rPc = new PointController(
      this.handShape,
      {
        // p: [vector.x, vector.y], //
        pOffset: [this.absX, this.absY],
        angle: this.rpcAngle,
        updateCircle: true,
        d: this.r,
        start: () => {
          this.unMovedR = this.r;
        },
        drag: ({ d }) => {
          this.r = d;
          this.handShape.refresh();
        },
        end: () => {
          this.rPc.patch({
            d: this.r,
          });
          this.handShape.saveSections();
        },
      },
      this.handShape.circleContainer,
      this.handShape.auxCircleContainer,
    );

    this.rPc.hide();

    this.pc = new PointController(
      this.handShape,
      {
        p: [this.x, this.y],
        pOffset: [this.offsetX, this.offsetY],
        updateCircle: false,
        controlPoint: {
          shapeIRI: this.handShape.IRI,
          name: this.id,
        },
      },
      this.handShape.circleContainer,
      this.handShape.auxCircleContainer,
    );
    this.pc.hide();

    if (this.amIFirst) {
      return;
    }

    /* console.log('curvePC', {
      pOffset: [
        this.sections[this.index - 1]?.x || 0,
        this.sections[this.index - 1]?.y || 0,
      ],
      relX: this.relX,
      relY: this.relY,
      x: this.x,
      y: this.y,
    }); */
    this.pc50 = new PointController(this.handShape, {
      controlPoint: {
        name: `${this.id}.50`,
        shapeIRI: this.handShape.IRI,
      },
      pOffset: [this.offsetX, this.offsetY],
      p: [this.x / 2, this.y / 2],
    });

    this.pc50.hide();

    // -- //
    return;
    this.d1PC = new PointController(this.handShape, {
      updateCircle: false,
      ds: 0,
      d: 1,
      angle: this.d1PCAngle,
      start: () => {},
      drag: ({ d }) => {
        for (let i = 0; i < this.d1Distances.length; i++) {
          const distance = this.d1Distances[i];
          if (d < distance) {
            // -- // -- //
            const [lowerLimit, upperLimit] = [
              this.d1Distances[i - 1] || 0,
              distance,
            ];

            this.d1 =
              0.1 * (i + 1 - (upperLimit - d) / (upperLimit - lowerLimit));
            /* console.log(
              'lowerLimit',
              lowerLimit,
              'upperLimit',
              upperLimit,
              'd',
              d,
            ); */
            // console.log('d1', this.d1);
            this.handShape.refreshElement();
            break;
          }
        }
        // console.log('d', d, 'this.d1', this.d1);
      },
      end: () => {
        this.save();
      },
    });
  }

  save() {
    this.handShape.updateSection(this.index, this.config);
  }

  dragBase: Coords;
  absDragBase: Coords;
  dragBaseAngle: number;
  dragBaseLength: number;
  dragBaseVector: BaseVector;
  constraints: DragConstraintInstance[];

  rotateMode = false;

  startDrag(
    shapeStore?: Record<string, boolean>,
    rotateMode = false,
  ): DragConstraintInstance[] {
    // this.rotateMode = rotateMode;

    this.baseStartDrag();

    /* this.constraints = this.handShape.getConstraintsByPoint(
      this.id,
      {
        [this.handShape.IRI]: true,
        ...shapeStore,
      },
      {
        rotate: true,
      },
    ); */

    /* this.constraints.map(({ shapeIRI, point }) => {
      const shape = this.handShape.service.getShapeByIRI(shapeIRI);
      shape.startDragBy(
        point,
        {
          [this.handShape.IRI]: true,
          ...shapeStore,
        },
        {
          rotate: true,
        },
      );
    }); */
    // -- // -- // -- // -- // -- // -- //
    if (this.handShape.isClosed) {
      switch (this.index) {
        case 0:
          if (this.cs.isShiftPressed) {
            // this.next.baseStartDrag();
            return [
              {
                shape: this.handShape,
                point: 'section.0',
                limit: {
                  x: -this.next.x,
                  y: -this.next.y,
                  l: this.next.l,
                },
              },
            ];
          } else {
            this.handShape.lastHandSection.startDrag();
            this.next.baseStartDrag();
          }

          break;
        case 1:
          if (this.cs.isShiftPressed) {
            this._next.baseStartDrag();
          } else {
            this._next._next.baseStartDrag();
          }
          break;
        case 2:
          if (!this.cs.isShiftPressed) {
            this.prev.baseStartDrag();
          }
          this.next.baseStartDrag();
          break;
        case 3:
          this.baseStartDrag();
          break;
      }
    } else {
      // if (!this.nextS)

      switch (this.index) {
        case 0:
          // -- //
          break;
        case 1:
          break;
        case 2:
          break;
        case 3:
          break;
      }

      this._next?.startDrag(shapeStore, rotateMode);
    }

    return [];
  }
  baseStartDrag() {
    this.dragBase = [this.x, this.y];
    this.absDragBase = [this.absoluteX, this.absoluteY];
    this.dragBaseAngle = this.handShape.getAngle(this.x, this.y);
    this.dragBaseLength = this.l;
    this.dragBaseVector = new BaseVector([this.x, this.y]);
  }

  drag(dx: number, dy: number, dAngle?: number) {
    this.handShape.sectionChanged = true;
    // -- // -- //

    this.dx = dx;
    this.dy = dy;
    const angleDiff =
      dAngle !== undefined
        ? dAngle
        : this.handShape.getAngle(this.x, this.y) - this.dragBaseAngle;

    this.constraints?.map(({ shape, point }) => {
      shape.dragBy(point, [dx, dy], angleDiff);
    });

    if (this.handShape.isClosed) {
      switch (this.index) {
        case 0:
          if (this.cs.isShiftPressed) {
            this.next.baseDrag(-dx, -dy);
          } else {
            this.handShape.lastHandSection.baseDrag(dx, dy);
            this.next.baseDrag(-dx, -dy);
          }
          break;
        case 1:
          this.baseDrag(dx, dy);
          if (this.cs.isShiftPressed) {
            this._next.baseDrag(-dx, -dy);
          } else {
            this._next.refreshPCs();
            this._next?._next?.baseDrag(-dx, -dy);
          }
          // if (this)

          break;
        case 2:
          if (this.cs.isShiftPressed) {
            this.baseDrag(dx, dy);
          } else {
            this.prev.baseDrag(dx, dy);
            this.baseDrag(0, 0);
          }
          this.next.baseDrag(-dx, -dy);
          break;

        case 3:
          this.baseDrag(dx, dy);
          // -- //
          break;
        default:
          // -- //
          break;
      }

      return;
    } else {
      this.baseDrag(dx, dy);
    }

    if (this.rotateMode) {
      // if (this.index == 0) {
      //   console.log('hs > drag 0', angleDiff);
      // }

      this._next?.dragByAngle(angleDiff);
    } else {
      this._next?.drag(dx, dy);
    }
  }

  dx: number;
  dy: number;

  baseDrag(dx: number, dy: number) {
    this.x = this.dragBase[0] + dx;
    this.y = this.dragBase[1] + dy;

    this.refreshPCs();
  }

  dragByAngle(angleDiff: number) {
    // if (this.index == 1) {
    //   console.log('hs(1) > dragByAnggle', angleDiff);
    // }

    const currentAngle = this.dragBaseAngle + angleDiff;
    this.x = this.dragBaseLength * Math.cos(currentAngle);
    this.y = this.dragBaseLength * Math.sin(currentAngle);

    const [x0, y0] = this.absDragBase;
    this.constraints.map(({ shape, point }) => {
      shape.dragBy(
        point,
        [
          this.handShape.currentPosition.x + this.absX - x0,
          this.handShape.currentPosition.y + this.absY - y0,
        ],
        angleDiff,
      );
    });

    this._next?.dragByAngle(angleDiff);
    this.refreshPCs();
  }

  _drag(
    constraint: DragConstraintInstance,
    { dx, dy, dAngle }: { dx: number; dy: number; dAngle?: number },
    root = false,
  ) {
    const { noTranslate, noAngle, inverse } = constraint;

    this.handShape.sectionChanged = true;

    const [x, y] = this.dragBase;

    let [adx, ady] = [0, 0];

    if (dAngle && !root && !inverse && !noAngle) {
      // const [adx, ady] = [ // -- // -- //
      const { x: xRotated, y: yRotated } = this.dragBaseVector
        .copy()
        .rotate(dAngle);
      adx = xRotated - x;
      ady = yRotated - y;

      dx += adx;
      dy += ady;
    }

    if (noTranslate && !inverse && !noAngle) {
      this.x = x + adx;
      this.y = y + ady;
    } else {
      this.x = x + (inverse ? -dx : dx);
      this.y = y + (inverse ? -dy : dy);
    }
    return [dx, dy];
  }

  endDrag() {
    // TODO - constraints the
    this.constraints?.map(({ shape, point }) => {
      shape.endDragBy(point);
    });
    switch (this.index) {
      case 0:
        console.log('save-translate', this.dx, this.dy);
        this.handShape.saveTranslate(this.dx, this.dy);
        break;
    }
    this._next?.endDrag();
  }

  refreshPCs() {
    this.pc?.patch({
      pOffset: [this.offsetX, this.offsetY],
      p: [this.x, this.y],
    });

    this.rPc?.patch({
      pOffset: [this.absX, this.absY],
      angle: this.rpcAngle,
      d: this.r,
    });

    this.pc50?.patch({
      pOffset: [this.offsetX, this.offsetY],
      p: [this.x / 2, this.y / 2],
    });
  }

  offset: Coords;
  lineStart: Coords;
  lineEnd: Coords;
  prevV: Vector;
  nextV: Vector;
  prevDiffAngle: number;
  nextDiffAngle: number;
  startPoint: Coords;
  endPoint: Coords;
  cornerPoint: Coords;
  cornerLineStart: Coords;
  cornerLineEnd: Coords;
  d1StartVector: Vector;
  d1EndVector: Vector;
  d2StartVector: Vector;
  d2EndVector: Vector;

  // These variables are relevant by the inner sections
  forwardMode: 'arc' | 'corner';

  get backwardMode() {
    return this.forwardMode == 'arc' ? 'corner' : 'arc';
  }

  get end() {
    return this.config.end;
  }

  get joint() {
    return this.config.joint;
  }

  get l() {
    return Math.sqrt(Math.pow(this.x, 2) + Math.pow(this.y, 2));
  }

  d1A1Points: Coords[];
  d1A2Points: Coords[];
  d1EndPoints: Coords[];

  d1Distances: number[];
  d1PCAngle: number;

  paths: PathElement[] = [];
  getCurveRCPoints() {
    // -- // -- //
    if (this.amIFirst) {
      return;
    }

    this.paths.map(path => path.remove());
    this.paths = [];

    const l = Math.sqrt(Math.pow(this.x, 2) + Math.pow(this.y, 2));
    const v1 = new BaseVector([this.x, this.y]).reScale(this.prev.r);
    v1.rotate(-PI / 2 + this.prev.diffAngle);

    const va1 = new BaseVector([this.x, this.y]).reScale(l / 3);
    va1.rotate(this.prev.diffAngle);

    const v2 = new Vector(
      {
        x: this.x,
        y: this.y,
      },
      {
        x: 0,
        y: 0,
      },
    ).reScale(this.r);
    v2.rotate(PI / 2 + this.prev.diffAngle);

    const va2 = new Vector(
      {
        x: this.x,
        y: this.y,
      },
      {
        x: 0,
        y: 0,
      },
    ).reScale(l / 3);
    va2.rotate(this.prev.diffAngle);

    // console.log('---start--- this.prev.diffAngle', this.prev.diffAngle); //
    // console.log('---start--- va2.getAngle', va2.getAngle()); //

    this.d1A1Points = [];
    this.d1A2Points = [];
    this.d1EndPoints = [];
    this.d1Distances = [];

    const [sx0, sy0] = v1._end;
    const [ex0, ey0] = v2._end;

    const vOuter = new Vector(v1.end, v2.end).scale(0.5);
    // -- // -- //
    const [ox, oy] = vOuter._end;
    //[this.x / 2, this.y / 2]; //

    const d = 0.1;

    const d1Points = [];
    let lastPoint: Coords;
    for (let i = 0; i < 8; i++) {
      // console.log('va2.getAngle', va1.getAngle());
      const [sx, sy] = v1._end;
      const [ex, ey] = v2._end;
      const [x, y] = [ex - sx, ey - sy];

      this.d1A1Points.push(va1._end);
      this.d1A2Points.push(va2._end);
      this.d1EndPoints.push([x, y]);

      const [_x, _y] = this.getConfigT(
        x,
        y,
        [va1.x, va1.y],
        [va2.x, va2.y],
        0.5,
      );

      // this.paths.push(
      //   new PathElement(this.handShape, this.handShape.container, {
      //     stroke: '0ff0000',
      //     'stroke-width': 1,
      //     position: {
      //       x: sx,
      //       y: sy,
      //     },
      //     elements: [
      //       {
      //         type: 'curve',
      //         bx: va1.x,
      //         by: va1.y,
      //         cx: va2.x,
      //         cy: va2.y,
      //         x,
      //         y,
      //       } as CurveItem,
      //     ],
      //   }),
      // );

      const p = [_x + sx, _y + sy] as Coords;
      // this.pcs.push(
      //   new PointController(this.handShape, {
      //     p,
      //     // pOffset: [ox, oy],
      //   }),
      // );

      lastPoint = p;
      d1Points.push(p);

      v1.rotate(-d);
      va1.rotate(-d);

      v2.rotate(d);
      va2.rotate(d);
    }

    const [startX, startY] = d1Points.shift();

    let prevPoint = [startX, startY];
    let currentDistance = 0;
    d1Points.map(([x, y]) => {
      const [xPrev, yPrev] = prevPoint;
      const distance = Math.sqrt(
        Math.pow(x - xPrev, 2) + Math.pow(y - yPrev, 2),
      );
      // -- // -- // -- //
      currentDistance += distance;
      this.d1Distances.push(currentDistance);
      prevPoint = [x, y];
    });

    // console.log('d1Distances', this.d1Distances);

    const [endX, endY] = lastPoint;
    const pcRange = Math.sqrt(
      Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2),
    );

    this.d1PCAngle = new Vector(
      {
        x: startX,
        y: startY,
      },
      {
        x: endX,
        y: endY,
      },
    ).getAngle();

    // -- // -- //
    const ratio = pcRange / currentDistance;
    this.d1Distances = this.d1Distances.map(val => val * ratio);

    const d1Index = Math.floor(this.d1 * 10) - 1;
    const remaining = (this.d1 * 10) % 1;
    const [lowerLimit, upperLimit] = [
      this.d1Distances[d1Index] || 0,
      this.d1Distances[d1Index + 1],
    ];

    const [_ox, _oy] = [this.prev.x || 0, this.prev.y || 0];

    this.d1PC?.patch({
      pOffset: [_ox + startX, _oy + startY],
      d: lowerLimit + remaining * (upperLimit - lowerLimit),
    });

    return;

    /*
    const [x0, y0] = [this.x / 2, this.y / 2];
    // const lastPoint = this.pcs[this.pcs.length - 1].p;
    const lastPoint = this.pcs.pop().p;

    const line = new Line(
      new Point(this.x / 2, this.y / 2),
      new Point(lastPoint[0], lastPoint[1]),
    );
    line.calc();

    console.log('line.m', line.m, 'b', line.b);

    new PointController(this.handShape, {
      p: [this.x / 2, this.y / 2],
    });

    console.log('point', [this.x / 2, this.y / 2], 'lastPoint', lastPoint);
    this.pcs.map(pc => {
      const [x, y] = pc.p;

      const p = line.mapPoint(x, y);
      new PointController(this.handShape, {
        p,
      });

      const vector = new Vector(
        {
          x: p[0],
          y: p[1],
        },
        {
          x: pc.p[0],
          y: pc.p[1],
        },
      );
      console.log('vecotr', vector.getAngle());
    });
    */
  }

  diffAngle: number;

  calcAngles() {
    this.prevV = new BaseVector(this.prevVectorCoords);
    this.nextV = new BaseVector(this.nextVectorCoords);

    this.prevDiffAngle = asin((this.prev.r - this.r) / this.prevV.length);
    this.nextDiffAngle = asin((this.r - this.next.r) / this.nextV.length);

    // if the next
    this.diffAngle = asin((this.r - this.next.r) / this.nextV.length);

    this.prevV.reScale(this.r);
    this.nextV.reScale(this.r);

    const length = this.l;

    if (this.amIFirst) {
      // console.log('prev-getAngle', this.prevV.getAngle());
      // -- // -- // -- //
      this.prevV.rotate(PI / 2);
      this.nextV.rotate(-PI / 2);

      if (this.next.d1) {
        this.nextV.rotate(-this.next.d1);
      }

      if (this.next.d2) {
        this.prevV.rotate(this.next.d2);
      }

      switch (this.end) {
        // case 'arc':
        //   console.log('end.arc', this.index, this.prevDiffAngle);
        //   // this.prevV.rotate(this.prevDiffAngle + this.prev.d2);
        //   this.prevV.rotate(this.prevDiffAngle);
        //   this.nextV.rotate(-this.nextDiffAngle);
        //   break;
        case 'half-arc':
        case 'half-arc-inverse':
          break;
        case 'flat':
          break;
        default:
          // diffAngle is negative if the this.r < this.next.r -> the arc must be smaller
          this.prevV.rotate(-this.diffAngle);
          this.nextV.rotate(this.diffAngle);
          break;
      }

      this.startPoint = [
        this.offsetX + this.x + this.prevV.x,
        this.offsetY + this.y + this.prevV.y,
      ];
      this.cornerPoint = this.startPoint;
      this.prevAngle = this.prevV.getAngle();
      this.endPoint = [
        this.offsetX + this.x + this.nextV.x,
        this.offsetY + this.y + this.nextV.y,
      ];
      // console.log('endPoint', this.endPoint);
      this.nextAngle = this.nextV.getAngle();
    } else if (this.amILast) {
      this.prevV.rotate(PI / 2);
      this.nextV.rotate(-PI / 2);
      // -- // -- //
      switch (this.end) {
        case 'arc':
          // console.log('end.arc', this.index, this.prevDiffAngle);
          // this.prevV.rotate(this.prevDiffAngle + this.prev.d2);
          this.prevV.rotate(this.prevDiffAngle + this.d1);
          this.nextV.rotate(-this.prevDiffAngle - this.d2);
          break;
        case 'half-arc':
        case 'half-arc-inverse':
          break;
        case 'flat':
          break;
      }

      this.startPoint = [
        this.offsetX + this.x + this.prevV.x,
        this.offsetY + this.y + this.prevV.y,
      ];
      this.prevAngle = this.prevV.getAngle();
      this.endPoint = [
        this.offsetX + this.x + this.nextV.x,
        this.offsetY + this.y + this.nextV.y,
      ];
      this.nextAngle = this.nextV.getAngle();

      this.getCurveRCPoints();

      // The first cannot have currently a d1
    } else {
      // rescale is needed to make it uniform

      const a1 = this.prevV.getAngle();
      const a2 = this.nextV.getAngle();

      const v = new BaseVector(
        [this.prevV.x + this.nextV.x, this.prevV.y + this.nextV.y].map(
          v => v / 2,
        ) as Coords,
      ).reScale(this.r);

      this.cornerPoint = [
        this.offsetX + this.x + v.x,
        this.offsetY + this.y + v.y,
      ];

      let cw = false;
      if (a1 < PI) {
        cw = a1 < a2 ? a2 < a1 + PI : false;
      } else {
        cw = a1 < a2 || a2 < a1 - PI;
      }

      if (cw) {
        this.forwardMode = 'corner';

        this.prevV.rotate(-PI / 2 - this.prevDiffAngle - this.d2);
        this.nextV.rotate(PI / 2 + this.nextDiffAngle + this.next.d2);

        this.endPoint = [
          this.offsetX + this.x + this.prevV.x,
          this.offsetY + this.y + this.prevV.y,
        ];
        this.nextAngle = this.prevV.getAngle();

        switch (this.joint) {
          case 'symm':
            break;
          case 'assym':
            // -- // -- // -- /_
            // console.log('assym > corner');
            const _prevV = this.prevV.copy();
            const vector = _prevV.copy().rotate(-PI / 2);
            this.cornerLineStart = [
              this.offsetX + this.x + this.prevV.x + vector.x,
              this.offsetY + this.y + this.prevV.y + vector.y,
            ];
            this.cornerLineEnd = this.endPoint;
            this.nextAngle = vector.getAngle();

            break;
          case 'assym-inverse':
            // -- // -- //
            // const _prevV = this.prevV.copy();
            // const vector = _prevV.copy().rotate(-PI / 2);
            // this.cornerLineStart = [
            //   this.x + this.prevV.x + vector.x,
            //   this.y + this.prevV.y + vector.y,
            // ];
            // this.cornerLineEnd = this.endPoint;
            // this.nextAngle = vector.getAngle();

            // -- // -- //
            break;
        }

        this.startPoint = [
          this.offsetX + this.x + this.nextV.x,
          this.offsetY + this.y + this.nextV.y,
        ];
        this.prevAngle = this.nextV.getAngle();
      } else {
        // outer point with arc
        // console.log('forward-mode-arc', this.index);
        this.forwardMode = 'arc';

        this.getCurveRCPoints();

        this.prevV.rotate(PI / 2 + this.prev.diffAngle + this.d1);
        this.nextV.rotate(-PI / 2 + this.diffAngle - this.next.d1);

        this.startPoint = [
          this.offsetX + this.x + this.prevV.x,
          this.offsetY + this.y + this.prevV.y,
        ];

        // console.log('startPoint', this.prev.endPoint);
        // -- // -- // -- // -- //

        this.prevAngle = this.prevV.getAngle();

        let noEndPointUpdate = false;

        switch (this.joint) {
          case 'assym':
            // console.log('assym > arc');

            const _prevV = this.prevV.copy();
            const vector = _prevV.copy().rotate(PI / 2);

            const vYoo = this.prevV.copy().rotate(-this.prevDiffAngle);
            const [dx, dy] = [this.prevV.x - vYoo.x, this.prevV.y - vYoo.y];

            this.cornerLineStart = [
              this.offsetX + this.x + this.prevV.x + vector.x - dx,
              this.offsetY + this.y + this.prevV.y + vector.y - dy,
            ];

            this.prevV.rotate(PI / 2);
            this.prevAngle = this.prevV.getAngle();

            const [x, y] = [this.x, this.y];

            const _vector = new BaseVector([this.x, this.y]).reScale(
              2 * this.r,
            );

            this.cornerLineEnd = [
              this.x + _vector.x / 2,
              this.y + _vector.y / 2,
            ];

            // -- //
            const [_x, _y] = [
              this.next.x - (this.x + _vector.x),
              this.next.y - (this.y + _vector.y),
            ];

            const aThis = new BaseVector([x, y]).getAngle();
            const aNext = new BaseVector([_x, _y]).getAngle();

            const _aNext = new BaseVector([
              this.next.x - this.x,
              this.next.y - this.y,
            ]).getAngle();

            // console.log('aNext', aNext, 'aThis', aThis);
            if (aThis < _aNext && _aNext - aThis < PI / 2) {
              const d = Math.sqrt(Math.pow(_x, 2) + Math.pow(_y, 2));

              this.CX = this.offsetX + this.x + _vector.x;
              this.CY = this.offsetY + this.y + _vector.y;

              const diffAngle = Math.acos(
                this.r / (d / (this.next.r / this.r + 1)),
              );

              console.log(
                'demon',
                d / (this.next.r / this.r - 1),
                'aNext',
                aNext,
                'diffAngle',
                diffAngle,
              );
              // -- // -- //
              const prevAngle = aThis - PI;

              this.prevAngle = aThis - PI;

              this.nextAngle = aNext + diffAngle - 2 * PI;

              if (this.nextAngle < 0) {
                this.nextAngle += 2 * PI;
              }

              noEndPointUpdate = true;
              this.antiClockwise = true;
            } else {
              this.antiClockwise = false;
              this.CX = this.offsetX + this.x;
              this.CY = this.offsetY + this.y;
            }
            this.X = this.CX + Math.cos(this.nextAngle) * this.r;
            this.Y = this.CY + Math.sin(this.nextAngle) * this.r;

            // -- // -- // -- //

            break;
        }
        // -- // -- //

        if (!noEndPointUpdate) {
          this.endPoint = [
            this.offsetX + this.x + this.nextV.x,
            this.offsetY + this.y + this.nextV.y,
          ];
          this.nextAngle = this.nextV.getAngle();
        }
      }
    }

    if (this.d1) {
      this.d1StartVector = new BaseVector([this.x, this.y])
        .rotate(this.prev.diffAngle - this.d1)
        .reScale(length / 3);

      this.d1EndVector = new BaseVector([-this.x, -this.y])
        .rotate(this.prev.diffAngle + this.d1)
        .reScale(length / 3);

      /*
      console.log(
        'd1EndVector',
        this.d1EndVector.getAngle(),
        'prev.diffAngle',
        this.prev.diffAngle,
        'd1',
        this.d1,
      ); */
    }

    if (this.d2) {
      this.d2StartVector = new BaseVector([-this.x, -this.y])
        .rotate(-this.prevDiffAngle - this.d2)
        .reScale(length / 3);

      this.d2EndVector = new BaseVector([this.x, this.y])
        .rotate(-this.prevDiffAngle + this.d2)
        .reScale(length / 3);
    }

    if (this.d1StartVector && this.index > 0) {
      const [sx, sy] = this.prev.endPoint;
      const [ex, ey] = this.startPoint;

      // -- // console.log('prev.startPoint', [sx, sy], 'startPoint', [ex, ey]); // -- //

      const [x, y] = this.getConfigT(
        ex - sx,
        ey - sy,
        [this.d1StartVector.x, this.d1StartVector.y],
        [this.d1EndVector.x, this.d1EndVector.y],
        0.5,
      );
    }
  }

  CX: number;
  CY: number;

  X: number;
  Y: number;

  antiClockwise: boolean;

  getForwardElements() {
    if (this.amIFirst) {
      return [
        this.flat
          ? ({
              index: this.index,
              type: 'line',
              // offset: this.startPoint,
              X: this.endPoint[0],
              Y: this.endPoint[1],
            } as PathItem)
          : ({
              index: this.index,
              type: 'arc',
              CX: 0,
              CY: 0,
              // -- // -- //
              a1: this.prevAngle,
              a2: this.nextAngle,
              r: this.r,
              offset: this.startPoint,
              X: this.endPoint[0],
              Y: this.endPoint[1],
            } as ArcItem),
      ];
    } else if (this.amILast) {
      // console.log('last prevAngle', this.prevAngle, 'next', this.nextAngle);
      return [
        // this will change upon d1

        this.d1
          ? ({
              type: 'curve',
              bx: this.d1StartVector.x,
              by: this.d1StartVector.y,
              cx: this.d1EndVector.x,
              cy: this.d1EndVector.y,
              X: this.startPoint[0],
              Y: this.startPoint[1],
            } as CurveItem)
          : ({
              type: 'line',
              X: this.startPoint[0],
              Y: this.startPoint[1],
            } as PathItem),
        this.flat
          ? ({
              index: this.index,
              type: 'line',
              X: this.endPoint[0],
              Y: this.endPoint[1],
            } as PathItem)
          : ({
              index: this.index,
              type: 'arc',
              CX: this.absX,
              CY: this.absY,
              r: this.r,
              a1: this.prevAngle,
              a2: this.nextAngle,
              X: this.endPoint[0],
              Y: this.endPoint[1],
            } as ArcItem),
      ];
    } else {
      if (this.forwardMode == 'arc') {
        const array = [
          this.d1
            ? ({
                type: 'curve',
                bx: this.d1StartVector.x,
                by: this.d1StartVector.y,
                cx: this.d1EndVector.x,
                cy: this.d1EndVector.y,
                X: this.startPoint[0],
                Y: this.startPoint[1],
              } as CurveItem)
            : {
                type: 'line',
                X: this.startPoint[0],
                Y: this.startPoint[1],
              },
        ] as PathItem[];

        if (this.joint == 'assym') {
          array.push(
            ...[
              {
                type: 'line',
                X: this.cornerLineStart[0],
                Y: this.cornerLineStart[1],
              } as PathItem,
              {
                type: 'line',
                X: this.cornerLineEnd[0],
                Y: this.cornerLineEnd[1],
              } as PathItem,
            ],
          );

          // -- // -- //

          // const [CX, CY] = [this.x + vector.x, this.y + vector.y];
          array.push({
            index: this.index,
            type: 'arc',
            CX: this.CX,
            CY: this.CY,
            r: this.r,
            X: this.X,
            Y: this.Y,
            a1: this.prevAngle,
            a2: this.nextAngle,
            antiClockwise: this.antiClockwise,
          } as ArcItem);
        } else {
          array.push({
            index: this.index,
            type: 'arc',
            CX: this.absX,
            CY: this.absY,
            r: this.r,
            X: this.endPoint[0],
            Y: this.endPoint[1],
            a1: this.prevAngle,
            a2: this.nextAngle,
            antiClockwise: false,
          } as ArcItem);
        }
        return array;
      } else {
        return [
          this.d1
            ? ({
                type: 'curve',
                bx: this.d1StartVector.x,
                by: this.d1StartVector.y,
                cx: this.d1EndVector.x,
                cy: this.d1EndVector.y,
                X: this.cornerPoint[0],
                Y: this.cornerPoint[1],
              } as CurveItem)
            : {
                type: 'line',
                X: this.cornerPoint[0],
                Y: this.cornerPoint[1],
              },
        ];
      }
    }
  }

  getConfigT(x: number, y: number, a1: Coords, a2: Coords, t: number): Coords {
    // const [ax, ay] = this.prevPS.absCoords; //
    // console.log('getConfig y', x, 'y', y); //
    // console.log('getConfig a1', a1, 'a2', a2); //

    const [a1x, a1y] = a1;
    const v11 = new Vector({ x: 0, y: 0 }, { x: a1x, y: a1y });

    const [a2x, a2y] = a2;

    const v13 = new Vector({ x: x + a2x, y: y + a2y }, cloneDeep({ x, y }));

    const v12 = new Vector(
      cloneDeep({ x: a1x, y: a1y }),
      cloneDeep({ x: x + a2x, y: y + a2y }),
    );

    const P11 = v11.scale(t).end;
    const P12 = v12.scale(t).end;
    const P13 = v13.scale(t).end;

    const v21 = new Vector(P11, P12);
    const v22 = new Vector(P12, P13);

    const P21 = v21.scale(t).end;
    const P22 = v22.scale(t).end;

    const v3 = new Vector(P21, P22);

    v3.scale(t);
    const { x: xn, y: yn } = v3.end;

    return [xn, yn];
  }

  getBackwardElements() {
    if (this.amIFirst) {
      if (this.flat) {
        return [
          {
            index: this.index,
            type: 'line',
            X: 0,
            Y: 0,
          },
        ];
      }
      return [];
    } else if (this.amILast) {
      return this.prev.forwardMode == 'arc'
        ? [
            this.d2
              ? {
                  type: 'curve',
                  bx: this.d2StartVector.x,
                  by: this.d2StartVector.y,
                  cx: this.d2EndVector.x,
                  cy: this.d2EndVector.y,
                  X: this.prev.cornerPoint[0],
                  Y: this.prev.cornerPoint[1],
                }
              : {
                  index: this.index,
                  type: 'line',
                  X: this.prev.cornerPoint[0],
                  Y: this.prev.cornerPoint[1],
                },
          ]
        : [
            this.d2
              ? {
                  type: 'curve',
                  bx: this.d2StartVector.x,
                  by: this.d2StartVector.y,
                  cx: this.d2EndVector.x,
                  cy: this.d2EndVector.y,
                  X: this.prev.startPoint[0],
                  Y: this.prev.startPoint[1],
                }
              : {},
          ];
    } else {
      if (this.forwardMode == 'corner') {
        const array = [];

        if (this.joint == 'assym') {
          array.push(
            ...[
              {
                index: this.index,
                type: 'arc',
                CX: this.absX,
                CY: this.absY,
                r: this.r,
                // X: this.endPoint[0],
                // Y: this.endPoint[1],
                a1: this.prevAngle,
                a2: this.nextAngle,
                antiClockwise: false,
              } as ArcItem,
              {
                type: 'line',
                X: this.cornerLineStart[0],
                Y: this.cornerLineStart[1],
              } as PathItem,
              {
                type: 'line',
                X: this.cornerLineEnd[0],
                Y: this.cornerLineEnd[1],
              } as PathItem,
            ],
          );
        } else {
          array.push({
            index: this.index,
            type: 'arc',
            CX: this.absX,
            CY: this.absY,
            r: this.r,
            X: this.endPoint[0],
            Y: this.endPoint[1],
            a1: this.prevAngle,
            a2: this.nextAngle,
            antiClockwise: false,
          } as ArcItem);
        }

        array.push(
          this.d2
            ? {
                type: 'curve',
                bx: this.d2StartVector.x,
                by: this.d2StartVector.y,
                cx: this.d2EndVector.x,
                cy: this.d2EndVector.y,
                // X: this.cornerPoint[0],
                // Y: this.cornerPoint[1],
                X:
                  this.prev.backwardMode == 'corner'
                    ? this.prev.cornerPoint?.[0]
                    : this.prev.startPoint[0],
                Y:
                  this.prev.backwardMode == 'corner'
                    ? this.prev.cornerPoint?.[1]
                    : this.prev.startPoint[1],
              }
            : {
                type: 'line',
                X: this.prev.startPoint[0],
                Y: this.prev.startPoint[1],
              },
        );

        return array;
      } else {
        // corner-mode
        return [
          this.d2
            ? {
                type: 'curve',
                bx: this.d2StartVector.x,
                by: this.d2StartVector.y,
                cx: this.d2EndVector.x,
                cy: this.d2EndVector.y,
                X:
                  this.prev.backwardMode == 'corner'
                    ? this.prev.cornerPoint?.[0]
                    : this.prev.startPoint[0],
                Y:
                  this.prev.backwardMode == 'corner'
                    ? this.prev.cornerPoint?.[1]
                    : this.prev.startPoint[1],
              }
            : {
                index: this.index,
                type: 'line',
                X: this.prev.cornerPoint[0],
                Y: this.prev.cornerPoint[1],
              },
        ];
      }
    }
  }

  startAnimation(config: HandShapeSectionDescriptorNew, division: number) {
    const { x, y } = config;
    this.xIncrement = new IncrementController(
      this.x,
      x,
      division,
      Easing.LINEAR,
    );

    this.yIncrement = new IncrementController(
      this.y,
      y,
      division,
      Easing.LINEAR,
    );
  }

  incrementAnimation(increment: number) {
    if (this.amIFirst) {
      return;
    }
    this.x = this.xIncrement.increment(increment);
    this.y = this.yIncrement.increment(increment);
  }

  remove() {
    this.pcs.map(pc => pc.remove());
    this.removeStrokeElements();
    this.startCircle?.remove();
    this.endCircle?.remove();
  }

  removeStrokeElements() {
    this.topStroke?.remove();
    this.sideStroke?.remove();
    this.bottomStroke?.remove();
  }

  initStrokeElements() {
    this.removeStrokeElements();
    let X: number, Y: number, offset: Coords;

    if (!this.config.stroke) {
      return;
    }

    const { color, width, top, side, bottom } = this.config.stroke;
    if (top) {
      const { start: st, end: et } = top;

      const vector = new Vector(
        {
          x: this.lineStart[0],
          y: this.lineStart[1],
        },
        {
          x: this.lineEnd[0],
          y: this.lineEnd[1],
        },
      );

      let [ox, oy] = this.lineStart;
      const [X, Y] = this.lineEnd;

      if (!isNaN(st) && !isNaN(et)) {
        // -- //
        if (st != 0) {
          const v = vector.copy();
          v.flip();
          v.scale(1 - st);
          ox = v.end.x;
          oy = v.end.y;
        }
      }

      this.topStroke = new PathElement(
        this.handShape,
        this.handShape.container,
        {
          elements: [
            {
              type: 'line',
              offset: [ox, oy],
              X,
              Y,
            },
          ],
          stroke: this.handShape.service.resolveColor(color),
          'stroke-width': width,
        },
      );
    }

    if (side) {
      const { start: ss, end: es } = side;
      // -- // -- //

      if (this.flat) {
        offset = this.offset;
        X = this.lineStart[0];
        Y = this.lineStart[1];
        if (ss != 0 || es != 1) {
          const vector = new Vector(
            {
              x: offset[0],
              y: offset[1],
            },
            {
              x: X,
              y: Y,
            },
          );
          vector.scale(es);
          X = vector.end.x;
          Y = vector.end.y;
        }

        this.sideStroke = new PathElement(
          this.handShape,
          this.handShape.container,
          {
            elements: [
              {
                type: 'line',
                offset,
                X,
                Y,
              },
            ],
            stroke: this.handShape.service.resolveColor(color),
            'stroke-width': width,
          },
        );
      }
    }

    if (bottom) {
      const { start: sb, end: eb } = bottom;

      offset = this.handShape.lastHandSection.lineEnd;

      let X = this.handShape.firstHandSection.offset[0];
      let Y = this.handShape.firstHandSection.offset[1];

      if (sb != 0 || eb != 1) {
        const vector = new Vector(
          {
            x: offset[0],
            y: offset[1],
          },
          {
            x: X,
            y: Y,
          },
        );
        vector.scale(eb);
        X = vector.end.x;
        Y = vector.end.y;
      }
      this.bottomStroke = new PathElement(
        this.handShape,
        this.handShape.container,
        {
          elements: [
            {
              type: 'line',
              offset,
              X,
              Y,
            },
          ],
          stroke: this.handShape.service.resolveColor(color),
          'stroke-width': width,
        },
      );
    }
  }
}
